import { A, Callout, ContactBlock, P, Section, Sub, Ul, card } from './LegalDoc'
import { CONTACT_EMAIL, OPERATOR_NAME } from './versions'

export default function PrivacyPolicyContent() {
  return (
    <div style={card}>
      <Section title="Introduction">
        <P>
          Branon is an educational platform operated by <strong>{OPERATOR_NAME}</strong> ("we",
          "us"). This Privacy Policy explains what information we collect, how we use it, and the
          choices available to you when you use Branon — including the Branon student apps
          (grades 6–10 and grades 11–12) and the Branon parent app.
        </P>
        <Callout tone="info" title="Which part applies to you?">
          <P>
            Branon is available in two ways, and what data we hold about you depends on which one
            applies:
          </P>
          <Ul
            items={[
              <>
                <strong>Part A — Access through your school.</strong> Your account was created by a
                school that licenses Branon.
              </>,
              <>
                <strong>Part B — Direct family subscription.</strong> A parent or guardian signed up
                and subscribed directly through the Branon parent app.
              </>,
            ]}
          />
          <P>The remaining sections after Parts A and B apply to everyone.</P>
        </Callout>
      </Section>

      <Section title="Part A — Access provided through your school">
        <Sub title="A1. Information provided by schools">
          <Ul
            items={[
              'Student name, grade level, and class/section',
              'School name and identification',
              'Teacher and school administrator account details',
              'School subscription and license information',
            ]}
          />
          <Callout tone="info">
            <P>
              For school-provided accounts, student data is supplied by the school — students do not
              register with us individually, and we do not collect student email addresses or parent
              contact details for these accounts.
            </P>
          </Callout>
        </Sub>
        <Sub title="A2. Consent and control">
          <Ul
            items={[
              'Schools are responsible for obtaining parental consent before enrolling students on Branon.',
              'Schools control account creation, deactivation, and deletion for their students.',
              'Students and parents in this model exercise data access, correction, and deletion rights through the school administration; you may also contact us directly.',
            ]}
          />
        </Sub>
      </Section>

      <Section title="Part B — Direct family subscriptions">
        <Sub title="B1. Information the parent provides">
          <P>
            The account holder must be a parent or guardian aged 18 or over. At sign-up we collect:
          </P>
          <Ul
            items={[
              'Email address (verified, used for login and account recovery)',
              'Password (stored only in securely hashed form)',
              'Phone number and country code',
              'Country of residence',
              'Confirmation that you are 18 or older',
              'A record of your acceptance of the Terms and this Privacy Policy (which document version, and when)',
            ]}
          />
        </Sub>
        <Sub title="B2. Child profiles — designed to minimise children's data">
          <P>
            Child profiles on family subscriptions are deliberately minimal. We ask parents to use a
            nickname for their child rather than the child's real name, and we do not request the
            child's real name, email, phone number, photograph, or date of birth.
          </P>
          <Ul
            items={[
              'Nickname / preferred display name (chosen by the parent)',
              'Grade level',
              'Login credentials: an auto-generated student ID and a PIN (the PIN is stored only in securely hashed form)',
            ]}
          />
        </Sub>
        <Sub title="B3. Information generated through use">
          <Ul
            items={[
              'Concepts studied, learning sessions, and practice attempts',
              'Test and exam-simulation attempts and results',
              'Questions asked to the AI assistant ("Chinni") and chat history',
              'Learning progress and mastery signals',
              'Study material the student uploads or pastes (photos, text, or PDFs) to get help with',
            ]}
          />
          <Callout tone="warn" title="About uploads">
            <P>
              Uploaded study material is sent to our AI providers to generate explanations and
              practice. Please do not include personal information (names, addresses, ID numbers) in
              uploaded photos or text — it is not needed for the platform to help.
            </P>
          </Callout>
        </Sub>
        <Sub title="B4. Payments">
          <Ul
            items={[
              'Payments are processed by Razorpay, our payment processor. Card, UPI, and bank details are handled by Razorpay and are not stored on our servers.',
              'We store your subscription status, plan, billing history, and payment references needed to manage your subscription.',
            ]}
          />
        </Sub>
        <Sub title="B5. Children's privacy and parental consent">
          <Ul
            items={[
              'Child profiles can only be created by the subscribing parent, inside the parent app.',
              'By creating a child profile, the parent provides consent for the child to use Branon and for the processing described in this policy.',
              'The nickname-only design means we hold as little personal data about children as the service allows.',
              'There are no social features, public profiles, or behavioural advertising anywhere on Branon.',
            ]}
          />
        </Sub>
        <Sub title="B6. Your rights (exercised directly with us)">
          <Ul
            items={[
              'Access and review your and your children’s data',
              'Correct inaccurate information (nicknames and account details can be edited in the parent app)',
              'Request deletion of a child profile or your entire family account',
              'Withdraw consent by cancelling your subscription and requesting deletion',
            ]}
          />
          <P>
            To exercise any of these rights, use the parent app or email us at{' '}
            <A href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</A>.
          </P>
        </Sub>
        <Sub title="B7. Emails we send">
          <P>
            We send transactional emails only (account confirmation, password reset, family member
            invitations, and billing notices). We do not send marketing email without your consent.
          </P>
        </Sub>
      </Section>

      <Section title="How we use information">
        <Ul
          items={[
            'Provide personalised learning experiences appropriate to the student’s grade',
            'Track learning progress and generate insights for parents, teachers, and schools',
            'Generate AI explanations, practice questions, and exam simulations',
            'Improve response quality and speed (including caching of previously generated content)',
            'Operate subscriptions and billing (family subscriptions only)',
            'Keep the platform secure and prevent abuse',
          ]}
        />
      </Section>

      <Section title="Third-party service providers">
        <P>Branon relies on the following processors to operate:</P>
        <Ul
          items={[
            <>
              <strong>Supabase</strong> — database, authentication, and hosting (
              <A href="https://supabase.com/privacy" external>
                privacy policy
              </A>
              )
            </>,
            <>
              <strong>Anthropic (Claude AI)</strong> — AI-generated explanations, answers, and
              practice content (
              <A href="https://www.anthropic.com/privacy" external>
                privacy policy
              </A>
              )
            </>,
            <>
              <strong>OpenAI</strong> — text embeddings used for similarity matching (
              <A href="https://openai.com/privacy" external>
                privacy policy
              </A>
              )
            </>,
            <>
              <strong>Razorpay</strong> — payment processing for family subscriptions (
              <A href="https://razorpay.com/privacy/" external>
                privacy policy
              </A>
              )
            </>,
            <>
              <strong>Resend</strong> — transactional email delivery for family accounts (
              <A href="https://resend.com/legal/privacy-policy" external>
                privacy policy
              </A>
              )
            </>,
          ]}
        />
        <P>
          Our infrastructure providers may process standard technical logs (such as IP address and
          browser type) transiently for security and reliability. Data processed by these providers
          may be stored or processed outside India.
        </P>
      </Section>

      <Section title="What we never do">
        <Ul
          items={[
            'Sell personal data',
            'Share data with advertisers or marketing companies',
            'Show advertising to students',
            'Use children’s data for profiling unrelated to learning',
          ]}
        />
      </Section>

      <Section title="Data storage, security, and retention">
        <Ul
          items={[
            'Login credentials (passwords and PINs) are stored only in securely hashed form',
            'Data is transmitted over HTTPS/TLS',
            'Access is restricted by role (student, parent, teacher, school administrator)',
            'School data is isolated per school; family data is isolated per family',
          ]}
        />
        <P>
          We retain data while an account is active, and for up to <strong>1 year</strong> after an
          account is closed or a subscription ends, after which it is deleted. You (or your school)
          may request earlier deletion at any time by contacting us.
        </P>
      </Section>

      <Section title="Cookies">
        <P>
          We use essential cookies only — those required for login sessions and platform
          functionality. We do not use advertising, marketing, or third-party analytics cookies.
        </P>
      </Section>

      <Section title="Compliance">
        <P>
          We work to comply with India's Digital Personal Data Protection Act, 2023 (DPDP Act) and
          educational data-privacy best practices. For school-provided access (Part A), the school
          determines the purposes of processing and we process student data on its behalf. For
          direct family subscriptions (Part B), {OPERATOR_NAME} is the data fiduciary and the
          subscribing parent gives consent directly.
        </P>
      </Section>

      <Section title="Changes to this policy">
        <Ul
          items={[
            'The "Last Updated" date at the top of this page shows the current version',
            'Material changes will be notified to schools (Part A) and to the parent account email (Part B)',
            'Continued use after changes take effect constitutes acceptance',
          ]}
        />
      </Section>

      <Section title="Contact and grievances">
        <P>
          For privacy questions, data requests, or grievances under applicable law, contact us — we
          respond within 48–72 hours:
        </P>
        <ContactBlock email={CONTACT_EMAIL} operator={OPERATOR_NAME} />
      </Section>
    </div>
  )
}
